import { Currency, Dtos, type UserId } from "@inrev/common";
import { DateTime } from "luxon";
import { type EntityScope, scopeMatches } from "../../../shared/scope";

type AccountPreview = Pick<
	Dtos.SuretyAccount.Preview.Get.Response,
	"status" | "hasBeenActive" | "underwriterUserId"
>;
type BondPreview = Pick<
	Dtos.SuretyBond.Preview.Get.Response,
	"status" | "type" | "amount" | "premium" | "effectiveDate" | "underwriterUserId"
>;

export type WrittenAccountMetricsInput = {
	accountPreviews: AccountPreview[];
	bondPreviews: BondPreview[];
	scope: EntityScope;
	currentUserId: UserId;
	nowMs?: number;
};

export type CurrencyTrend = {
	series: number[];
	current: Currency;
	prior: Currency;
};

export type WrittenAccountMetrics = {
	total: number;
	active: number;
	ttmGwp: CurrencyTrend;
	totalOpenExposure: Currency;
	openExposureTrend: CurrencyTrend;
	avgOpenExposure: Currency;
};

const TREND_SERIES_BUCKETS = 12;
const MS_PER_DAY = 86_400_000;
const OPEN_EXPOSURE_WINDOW_DAYS = 30;

type CurrencyEvent = { atMs: number; weight: Currency };

const toMs = (value: string): number => DateTime.fromISO(value).toMillis();

const buildMonthlyCurrencyTrend = (events: CurrencyEvent[], nowMs: number): CurrencyTrend => {
	const now = DateTime.fromMillis(nowMs);
	const series: Currency[] = new Array<Currency>(TREND_SERIES_BUCKETS).fill(Currency.ZERO);
	let current: Currency = Currency.ZERO;
	let prior: Currency = Currency.ZERO;

	for (const { atMs, weight } of events) {
		const monthsAgo = Math.floor(now.diff(DateTime.fromMillis(atMs), "months").months);
		if (monthsAgo >= 0 && monthsAgo < TREND_SERIES_BUCKETS) {
			const bucket = TREND_SERIES_BUCKETS - 1 - monthsAgo;
			series[bucket] = Currency.add(series[bucket], weight);
			current = Currency.add(current, weight);
		} else if (monthsAgo >= TREND_SERIES_BUCKETS && monthsAgo < TREND_SERIES_BUCKETS * 2) {
			prior = Currency.add(prior, weight);
		}
	}

	return { series, current, prior };
};

const buildWindowedCurrencyTrend = (events: CurrencyEvent[], nowMs: number): CurrencyTrend => {
	const windowMs = OPEN_EXPOSURE_WINDOW_DAYS * MS_PER_DAY;
	const windowStartMs = nowMs - windowMs;
	const priorStartMs = nowMs - windowMs * 2;
	const bucketMs = windowMs / TREND_SERIES_BUCKETS;

	const series: Currency[] = new Array<Currency>(TREND_SERIES_BUCKETS).fill(Currency.ZERO);
	let current: Currency = Currency.ZERO;
	let prior: Currency = Currency.ZERO;

	for (const { atMs, weight } of events) {
		if (atMs >= windowStartMs && atMs <= nowMs) {
			current = Currency.add(current, weight);
			const bucket = Math.min(
				TREND_SERIES_BUCKETS - 1,
				Math.max(0, Math.floor((atMs - windowStartMs) / bucketMs)),
			);
			series[bucket] = Currency.add(series[bucket], weight);
		} else if (atMs >= priorStartMs && atMs < windowStartMs) {
			prior = Currency.add(prior, weight);
		}
	}

	return { series, current, prior };
};

export const computeWrittenAccountMetrics = ({
	accountPreviews,
	bondPreviews,
	scope,
	currentUserId,
	nowMs,
}: WrittenAccountMetricsInput): WrittenAccountMetrics => {
	const now = nowMs ?? DateTime.now().toMillis();
	const scopeMatch = (underwriterUserId: UserId) =>
		scopeMatches(scope, underwriterUserId, currentUserId);

	const scopedAccounts = accountPreviews.filter((account) => scopeMatch(account.underwriterUserId));
	const total = scopedAccounts.filter((account) => account.hasBeenActive).length;
	const active = scopedAccounts.filter(
		(account) => account.hasBeenActive && account.status === "active",
	).length;

	const scopedBonds = bondPreviews.filter((bond) => scopeMatch(bond.underwriterUserId));

	const ttmGwp = buildMonthlyCurrencyTrend(
		scopedBonds.map((bond) => ({ atMs: toMs(bond.effectiveDate), weight: bond.premium })),
		now,
	);

	const openExposureBonds = scopedBonds.filter(
		(bond) => bond.status === "open" && bond.type === "final",
	);
	const totalOpenExposure = Currency.sum(openExposureBonds.map((bond) => bond.amount));
	const openExposureTrend = buildWindowedCurrencyTrend(
		openExposureBonds.map((bond) => ({ atMs: toMs(bond.effectiveDate), weight: bond.amount })),
		now,
	);

	const avgOpenExposure = total === 0 ? Currency.ZERO : Currency.round(totalOpenExposure / total);

	return {
		total,
		active,
		ttmGwp,
		totalOpenExposure,
		openExposureTrend,
		avgOpenExposure,
	};
};
